import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api, notifyProfileImageApprovalPending } from '../api';
import { logHttpError } from '../utils/httpError';
import { Paper, Typography, Box, CircularProgress, Button, Card, CardContent, CardMedia, Alert, Grid, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import { Link } from 'react-router-dom';

// Group flat image array into per-user objects
function groupByUser(images) {
  const map = new Map();
  for (const img of images) {
    const userId = img.user_id;
    if (!map.has(userId)) {
      map.set(userId, {
        user_id: userId,
        username: img.user?.username,
        is_creator: img.user?.is_creator,
        is_approved_creator: img.user?.is_approved_creator,
        humanVerificationVideos: img.user?.humanVerificationVideos || [],
        images: [],
      });
    }
    map.get(userId).images.push(img);
  }
  return Array.from(map.values());
}

export default function ApproveProfileImages() {
  const theme = useTheme();
  const [rawImages, setRawImages] = useState([]);
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(new Set());
  const [results, setResults] = useState({});
  const processingRef = useRef(new Set());

  const users = useMemo(() => groupByUser(rawImages), [rawImages]);

  const fetchPendingImages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/pending-user-images');
      const data = res.data?.data || [];
      setRawImages(data);
      setCurrentUserIndex(0);
      setProcessing(new Set());
      processingRef.current = new Set();
      setResults({});
      notifyProfileImageApprovalPending(data.length > 0);
    } catch (err) {
      logHttpError(err, 'Fetch pending images');
      setError('Failed to fetch pending images. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingImages();
  }, [fetchPendingImages]);

  const currentUser = users[currentUserIndex];
  const currentUserImages = currentUser?.images || [];

  // Count remaining pending images for current user (exclude already-actioned)
  const remainingImages = currentUserImages.filter(img => !results[img.id]);

  // Auto-advance when all images for current user are actioned
  useEffect(() => {
    if (!currentUser) return;
    if (currentUserImages.length > 0 && remainingImages.length === 0) {
      const timer = setTimeout(() => {
        goNextUser();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [remainingImages.length, currentUser]);

  const goNextUser = useCallback(() => {
    setResults({});
    setCurrentUserIndex(prev => prev + 1);
  }, []);

  const handleApprove = useCallback(async (imageId) => {
    if (processingRef.current.has(imageId)) return;
    processingRef.current = new Set([...processingRef.current, imageId]);
    setProcessing(new Set(processingRef.current));
    try {
      await api.post(`/admin/approve-user-image/${imageId}`);
      setResults(prev => ({ ...prev, [imageId]: { success: true, action: 'approved' } }));
    } catch (err) {
      const details = logHttpError(err, 'Approve Image');
      const msg = details?.data?.message || err?.message || 'Failed to approve image';
      setResults(prev => ({ ...prev, [imageId]: { success: false, message: msg } }));
    } finally {
      processingRef.current = new Set([...processingRef.current].filter(id => id !== imageId));
      setProcessing(new Set(processingRef.current));
    }
  }, []);

  const handleReject = useCallback(async (imageId) => {
    if (processingRef.current.has(imageId)) return;
    processingRef.current = new Set([...processingRef.current, imageId]);
    setProcessing(new Set(processingRef.current));
    try {
      await api.post(`/admin/reject-user-image/${imageId}`);
      setResults(prev => ({ ...prev, [imageId]: { success: true, action: 'rejected' } }));
    } catch (err) {
      const details = logHttpError(err, 'Reject Image');
      const msg = details?.data?.message || err?.message || 'Failed to reject image';
      setResults(prev => ({ ...prev, [imageId]: { success: false, message: msg } }));
    } finally {
      processingRef.current = new Set([...processingRef.current].filter(id => id !== imageId));
      setProcessing(new Set(processingRef.current));
    }
  }, []);

  const handleApproveAll = useCallback(async () => {
    if (!currentUser) return;
    const toApprove = currentUserImages.filter(img => !results[img.id]);
    for (const img of toApprove) {
      await handleApprove(img.id);
    }
  }, [currentUser, currentUserImages, results, handleApprove]);

  // Keyboard shortcuts: A = approve all, N = next user
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key === 'a') handleApproveAll();
      if (key === 'n') goNextUser();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleApproveAll, goNextUser]);

  if (loading) return <LoadingBlock height={420} />;

  if (error) {
    return (
      <>
        <PageHeader title="Profile Picture Approval" />
        <Paper sx={{ p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </>
    );
  }

  if (users.length === 0 || currentUserIndex >= users.length) {
    return (
      <>
        <PageHeader title="Profile Picture Approval" />
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5">All images have been reviewed!</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={fetchPendingImages}>Refresh</Button>
        </Paper>
      </>
    );
  }

  const verificationVideo = currentUser.humanVerificationVideos?.[0];

  return (
    <>
      <PageHeader title="Profile Picture Approval" />
      <Paper sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
        {/* User info bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">
            User:{' '}
            <Link
              to={`/user/${currentUser.user_id}`}
              state={{ from: { label: 'Approve Pictures', path: '/admin/picture-approval' } }}
              style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 700 }}
            >
              {currentUser.username || `User #${currentUser.user_id}`}
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">ID: {currentUser.user_id}</Typography>
          {currentUser.is_creator ? (
            <Chip label={currentUser.is_approved_creator ? 'Approved Creator' : 'Creator'} color={currentUser.is_approved_creator ? 'success' : 'primary'} size="small" />
          ) : (
            <Chip label="Fan" color="secondary" size="small" />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            User {currentUserIndex + 1} of {users.length}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Left: Pending images */}
          <Grid item xs={12} md={verificationVideo ? 8 : 12}>
            <Grid container spacing={2}>
              {currentUserImages.map((img) => {
                const result = results[img.id];
                const isProcessing = processing.has(img.id);

                return (
                  <Grid item xs={6} sm={4} md={verificationVideo ? 4 : 3} key={img.id}>
                    <Card sx={{
                      opacity: result?.success ? 0.5 : 1,
                      transition: 'opacity 0.3s',
                    }}>
                      {img.secureUrl ? (
                        <CardMedia
                          component="img"
                          image={img.secureUrl}
                          alt={`Image ${img.id}`}
                          sx={{ height: 280, objectFit: 'cover', bgcolor: '#f0f0f0' }}
                        />
                      ) : (
                        <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0' }}>
                          <Typography color="text.secondary">Image not available</Typography>
                        </Box>
                      )}
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" color="text.secondary">ID: {img.id}</Typography>
                        {result ? (
                          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 1, py: 0 }}>
                            {result.success ? (result.action === 'approved' ? 'Approved' : 'Rejected') : result.message}
                          </Alert>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              fullWidth
                              onClick={() => handleApprove(img.id)}
                              disabled={isProcessing}
                            >
                              {isProcessing ? <CircularProgress size={18} /> : 'Approve'}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              fullWidth
                              onClick={() => handleReject(img.id)}
                              disabled={isProcessing}
                            >
                              {isProcessing ? <CircularProgress size={18} /> : 'Reject'}
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          {/* Right: Verification video */}
          {verificationVideo && (
            <Grid item xs={12} md={4}>
              <Card>
                <Typography variant="subtitle2" sx={{ p: 1, bgcolor: '#f5f5f5', textAlign: 'center', fontWeight: 600 }}>
                  Verification Video
                </Typography>
                {verificationVideo.secureUrl ? (
                  <Box
                    component="video"
                    src={verificationVideo.secureUrl}
                    autoPlay
                    loop
                    muted
                    sx={{ width: '100%', maxHeight: 500, objectFit: 'contain', bgcolor: '#000' }}
                  />
                ) : (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0' }}>
                    <Typography color="text.secondary">Video not available</Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Bottom actions */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <Button
            size="large"
            variant="contained"
            color="success"
            onClick={handleApproveAll}
            disabled={remainingImages.length === 0}
          >
            Approve All ({remainingImages.length})
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={goNextUser}
          >
            Next User
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          Keyboard: A = Approve All, N = Next User
        </Typography>
      </Paper>
    </>
  );
}
